import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchProducts, fetchCategories } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Funnel } from "@phosphor-icons/react";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "";
  const isDeal = params.get("is_deal");

  useEffect(() => {
    fetchCategories().then((d) => setCategories(d.categories || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (category && category !== "all") query.category = category;
    if (search) query.search = search;
    if (sort) query.sort = sort;
    if (isDeal === "true") query.is_deal = true;
    fetchProducts(query)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category, search, sort, isDeal]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(params);
    if (!v || v === "all") np.delete(k);
    else np.set(k, v);
    setParams(np);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="catalog-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono uppercase text-xs tracking-[0.2em] text-[#002FA7] mb-2">CATALOG</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
            {search ? `Results for "${search}"` : category !== "all" ? category : "All gear"}
          </h1>
          <p className="font-mono text-sm text-zinc-500 mt-2" data-testid="catalog-count">
            {loading ? "Loading..." : `${products.length} items`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-8 items-center border border-zinc-200 p-4">
        <Funnel size={18} weight="bold" />
        <span className="font-mono uppercase text-xs tracking-[0.2em]">Filter</span>

        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="w-44 rounded-none border-zinc-300 font-mono text-sm" data-testid="filter-category">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sort || "default"} onValueChange={(v) => setParam("sort", v === "default" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-none border-zinc-300 font-mono text-sm" data-testid="filter-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top Rated</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={(e) => setParam("search", e.target.value)}
          className="w-56 rounded-none border-zinc-300 font-mono text-sm"
          data-testid="catalog-search-input"
        />

        <label className="ml-auto flex items-center gap-2 font-mono text-xs uppercase tracking-wider cursor-pointer">
          <input
            type="checkbox"
            checked={isDeal === "true"}
            onChange={(e) => setParam("is_deal", e.target.checked ? "true" : "")}
            data-testid="filter-deals-checkbox"
            className="w-4 h-4 accent-[#FF2A00]"
          />
          Deals only
        </label>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="border border-zinc-200 bg-zinc-50 aspect-[3/4] animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-24 border border-zinc-200" data-testid="catalog-empty">
          <p className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">
            No products match your filters.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="catalog-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}
